import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { clientAPI } from '../services/api';
import BatteryHealthModal from './BatteryHealthModal';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.camel.optitrac.com';
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface NoteEntry {
  text: string; user: string; timestamp: string;
}
interface AlertVehicle {
  vehicleId: string; fleet: string; licensePlate: string; name: string; make: string;
  unitId: string; lastTransmission: string; platform: 'Camel' | 'Starlink'; hoursAgo: number;
  alertId?: string; notes?: NoteEntry[]; reason?: string;
  vin?: string; vbat?: number; css?: number; spdk?: number;
}
interface DeviceDetail {
  serial_number?: string; imei_number?: string; iccid?: string; device_type?: string;
  device_manufacturer?: string; sim_data_plan?: string; model_version?: string;
  hex_value?: string; client_name?: string; vehicle_id?: string; licensePlate?: string;
}
interface HistoryItem {
  id: string; unit_id: string; platform: string; vehicle_name: string; fleet: string;
  license_plate: string; notes: string; created_at: string; resolved_at: string;
  resolved_by: string; created_by: string;
}

const DetailRow = ({ label, value }: { label: string; value?: string }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
    <span style={{ color: '#999', fontSize: '13px' }}>{label}</span>
    <span style={{ color: value ? '#333' : '#ccc', fontSize: '13px', fontWeight: 500 }}>{value || '—'}</span>
  </div>
);

const Alerts: React.FC<{ onNavigateToPlatform?: (platform: 'camel' | 'starlink', hexValue: string) => void }> = ({ onNavigateToPlatform }) => {
  const [alerts, setAlerts] = useState<AlertVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [platformFilter, setPlatformFilter] = useState<'all' | 'Camel' | 'Starlink'>('all');
  const [selectedAlert, setSelectedAlert] = useState<AlertVehicle | null>(null);
  const [deviceMap, setDeviceMap] = useState<Record<string, DeviceDetail>>({});
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalNotes, setModalNotes] = useState<NoteEntry[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [warnings, setWarnings] = useState<Record<string, { lowVin?: number; eid9?: boolean }>>({});
  const [warningFilter, setWarningFilter] = useState<'all' | 'warnings' | 'hide'>('all');
  const [hideBattery, setHideBattery] = useState(false);
  const [hideNotes, setHideNotes] = useState(false);
  const [fleetFilter, setFleetFilter] = useState<string>('all');
  const [vehicleHistory, setVehicleHistory] = useState<any[] | null>(null);
  const [vehicleHistoryLoading, setVehicleHistoryLoading] = useState(false);
  const [vehicleWarning, setVehicleWarning] = useState<{ lowVin?: number; eid9?: boolean } | null>(null);
  const [batteryModalVehicle, setBatteryModalVehicle] = useState<AlertVehicle | null>(null);

  const warningCount = alerts.filter(a => warnings[a.unitId]).length;
  const alertCountExcludingBattery = alerts.length - warningCount;
  const notesCount = alerts.filter(a => a.notes && a.notes.length > 0).length;
  const needsAttention = alerts.length - warningCount - notesCount + alerts.filter(a => warnings[a.unitId] && a.notes && a.notes.length > 0).length;
  const filteredAlerts = (() => {
    let list = platformFilter === 'all' ? alerts : alerts.filter(a => a.platform === platformFilter);
    if (warningFilter === 'warnings') list = list.filter(a => warnings[a.unitId]);
    if (hideBattery) list = list.filter(a => !warnings[a.unitId]);
    if (hideNotes) list = list.filter(a => !a.notes || a.notes.length === 0);
    if (fleetFilter !== 'all') list = list.filter(a => (a.fleet || 'Unknown') === fleetFilter);
    return list;
  })();
  const fleetCounts = alerts.reduce((acc, a) => { const f = a.fleet || 'Unknown'; acc[f] = (acc[f] || 0) + 1; return acc; }, {} as Record<string, number>);

  const formatTime = (iso: string) => {
    if (!iso) return 'Unknown';
    return new Date(iso).toLocaleString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const urgencyColor = (h: number) => h < 0 ? '#7c3aed' : h >= 72 ? '#dc2626' : h >= 48 ? '#ea580c' : h >= 24 ? '#f59e0b' : '#6b7280';
  const getDevice = (unitId: string) => deviceMap[unitId?.toUpperCase()] || null;

  const fetchDeviceMap = async () => {
    try {
      const r = await clientAPI.getAll();
      const map: Record<string, DeviceDetail> = {};
      (r.data || []).forEach((c: any) => (c.devices || []).forEach((d: any) => {
        if (d.hex_value) map[d.hex_value.toUpperCase()] = {
          serial_number: d.serial_number, imei_number: d.imei_number, iccid: d.iccid,
          device_type: d.device_type, device_manufacturer: d.device_manufacturer,
          sim_data_plan: d.sim_data_plan, model_version: d.model_version,
          hex_value: d.hex_value, client_name: c.name, vehicle_id: d.vehicle_id, licensePlate: d.license_plate || d.vehicle_id
        };
      }));
      setDeviceMap(map);
    } catch (e) { console.error('Error fetching devices:', e); }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [camelRes] = await Promise.allSettled([axios.get(`${API_BASE}/api/camel/vehicles`, { headers })]);

      let allStarlink: any[] = [];
      try {
        let page = 1;
        while (true) {
          const r = await axios.get(`${API_BASE}/api/starlink/vehicles`, { headers, params: { page, limit: 40 } });
          const v = r.data.vehicles || [];
          allStarlink = allStarlink.concat(v);
          if (v.length < 40) break;
          page++;
        }
      } catch (e) { console.error('Starlink fetch error:', e); }

      // Get existing active alerts from DB
      let activeAlerts: any[] = [];
      try {
        const r = await axios.get(`${API_BASE}/api/alerts/active`, { headers });
        activeAlerts = r.data || [];
      } catch (e) { console.error('Error fetching active alerts:', e); }
      const alertMap = new Map(activeAlerts.map((a: any) => [a.unit_id, a]));

      const now = Date.now();
      const offline: AlertVehicle[] = [];
      const onlineUnitIds = new Set<string>();

      const eid9UnitIds: string[] = [];

      if (camelRes.status === 'fulfilled') {
        (camelRes.value.data || []).forEach((v: any) => {
          const existing = alertMap.get(v.unitId);
          // EID 9 = battery disconnect — always alert regardless of online status
          if (v.eid === 9 || v.eid === '9') {
            eid9UnitIds.push(v.unitId);
            const diff = v.lastTransmission ? now - new Date(v.lastTransmission).getTime() : 0;
            offline.push({ vehicleId: v.vehicleId, fleet: v.fleet, licensePlate: v.licensePlate, name: v.name, make: v.make, unitId: v.unitId, lastTransmission: v.lastTransmission, platform: 'Camel', hoursAgo: diff ? Math.round(diff / 3600000) : 0, alertId: existing?.id, notes: Array.isArray(existing?.notes) ? existing.notes : [], vin: v.vin || '', vbat: v.batteryVoltage, css: v.css, spdk: v.speedKmh, reason: 'Battery Disconnected (EID 9)' });
            return;
          }
          if (v.online) { onlineUnitIds.add(v.unitId); return; }
          if (!v.lastTransmission) return;
          const diff = now - new Date(v.lastTransmission).getTime();
          offline.push({ vehicleId: v.vehicleId, fleet: v.fleet, licensePlate: v.licensePlate, name: v.name, make: v.make, unitId: v.unitId, lastTransmission: v.lastTransmission, platform: 'Camel', hoursAgo: Math.round(diff / 3600000), alertId: existing?.id, notes: Array.isArray(existing?.notes) ? existing.notes : [], vin: v.vin || '', vbat: v.batteryVoltage, css: v.css, spdk: v.speedKmh, reason: 'Offline' });
        });
      }

      allStarlink.forEach((v: any) => {
        const lt = v.lastTransmission || '';
        const isOffline = v.online === 'Disconnected' || (!lt && v.online !== 'Active' && v.online !== 'Parked');
        if (!isOffline) { onlineUnitIds.add(v.unitId); return; }
        const diff = lt ? now - new Date(lt).getTime() : 0;
        const existing = alertMap.get(v.unitId);
        offline.push({ vehicleId: v.vehicleId || v.id?.toString(), fleet: v.fleet || '', licensePlate: v.licensePlate || v.name || '', name: v.name || '', make: v.make || '', unitId: v.unitId || '', lastTransmission: lt, platform: 'Starlink', hoursAgo: lt ? Math.round(diff / 3600000) : -1, alertId: existing?.id, notes: Array.isArray(existing?.notes) ? existing.notes : [], reason: 'Offline' });
      });

      // Auto-resolve alerts for vehicles no longer alerting (back online or EID fixed)
      const currentAlertUnitIds = new Set(offline.map(a => a.unitId));
      activeAlerts.forEach(async (alert: any) => {
        if (!currentAlertUnitIds.has(alert.unit_id)) {
          try { await axios.put(`${API_BASE}/api/alerts/${alert.id}/resolve`, { auto: true }, { headers }); }
          catch (e) { console.error('Auto-resolve error:', e); }
        }
      });

      offline.sort((a, b) => (a.hoursAgo === -1 ? Infinity : a.hoursAgo) - (b.hoursAgo === -1 ? Infinity : b.hoursAgo));
      setAlerts(offline);
      setLastRefresh(new Date());

      // Check warnings — Camel offline + ALL Starlink (to catch EID 9 on online vehicles)
      const camelUnitIds = offline.filter(a => a.platform === 'Camel').map(a => a.unitId);
      const allStarlinkUnitIds = allStarlink.map((v: any) => v.unitId).filter(Boolean);
      const allWarnings: Record<string, { lowVin?: number; eid9?: boolean }> = {};
      // Mark Camel EID 9 vehicles we already caught
      eid9UnitIds.forEach(id => { allWarnings[id] = { ...allWarnings[id], eid9: true }; });
      const warnPromises = [];
      if (camelUnitIds.length > 0) warnPromises.push(axios.post(`${API_BASE}/api/camel/warnings`, { unitIds: camelUnitIds }, { headers }).then(r => Object.assign(allWarnings, r.data)).catch(() => {}));
      if (allStarlinkUnitIds.length > 0) warnPromises.push(axios.post(`${API_BASE}/api/starlink/warnings`, { unitIds: allStarlinkUnitIds }, { headers }).then(r => {
        const w = r.data || {};
        Object.assign(allWarnings, w);
        // Add online Starlink vehicles with EID 9 to alerts
        allStarlink.forEach((v: any) => {
          if (w[v.unitId]?.eid9 && !offline.find(a => a.unitId === v.unitId)) {
            const existing = alertMap.get(v.unitId);
            const lt = v.lastTransmission || '';
            const diff = lt ? now - new Date(lt).getTime() : 0;
            offline.push({ vehicleId: v.vehicleId || v.id?.toString(), fleet: v.fleet || '', licensePlate: v.licensePlate || v.name || '', name: v.name || '', make: v.make || '', unitId: v.unitId || '', lastTransmission: lt, platform: 'Starlink', hoursAgo: lt ? Math.round(diff / 3600000) : 0, alertId: existing?.id, notes: Array.isArray(existing?.notes) ? existing.notes : [], reason: 'Battery Disconnected (EID 9)' });
          }
        });
        setAlerts([...offline].sort((a, b) => (a.hoursAgo === -1 ? Infinity : a.hoursAgo) - (b.hoursAgo === -1 ? Infinity : b.hoursAgo)));
      }).catch(() => {}));
      await Promise.all(warnPromises);
      setWarnings(allWarnings);
      // Update reasons based on warnings
      offline.forEach(a => {
        const w = allWarnings[a.unitId];
        if (w?.eid9) a.reason = 'Battery Disconnected (EID 9)';
        else if (w?.lowVin && w.lowVin <= 10.10) a.reason = `Low Voltage (${w.lowVin.toFixed(2)}V)`;
      });
      // Auto-create alert records for vehicles without one (sequential to avoid duplicates)
      for (const a of offline.filter(a => !a.alertId)) {
        try {
          const r = await axios.post(`${API_BASE}/api/alerts`, {
            unitId: a.unitId, platform: a.platform, vehicleId: a.vehicleId,
            fleet: a.fleet, name: a.name, licensePlate: a.licensePlate, make: a.make,
            lastTransmission: a.lastTransmission, reason: a.reason || 'Offline'
          }, { headers });
          a.alertId = r.data.id;
        } catch (e) {}
      }
    } catch (error) { console.error('Error fetching alerts:', error); }
    finally { setLoading(false); }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/api/alerts/history`, { headers: getHeaders() });
      setHistory(r.data || []);
    } catch (e) { console.error('Error fetching history:', e); }
    finally { setHistoryLoading(false); }
  };

  const openVehicleHistory = async (unitId: string) => {
    setVehicleHistoryLoading(true);
    setVehicleHistory([]);
    setVehicleWarning(null);
    try {
      const r = await axios.get(`${API_BASE}/api/alerts/vehicle/${unitId}`, { headers: getHeaders() });
      const items = r.data || [];
      setVehicleHistory(items);
      const platform = items[0]?.platform;
      if (platform) {
        const ep = platform === 'Camel' ? 'camel' : 'starlink';
        const wr = await axios.post(`${API_BASE}/api/${ep}/warnings`, { unitIds: [unitId] }, { headers: getHeaders() }).catch(() => null);
        if (wr?.data?.[unitId]) setVehicleWarning(wr.data[unitId]);
      }
    } catch (e) { console.error('Error fetching vehicle history:', e); }
    finally { setVehicleHistoryLoading(false); }
  };

  const ensureAlert = async (v: AlertVehicle): Promise<string> => {
    if (v.alertId) return v.alertId;
    try {
      const r = await axios.post(`${API_BASE}/api/alerts`, {
        unitId: v.unitId, platform: v.platform, vehicleId: v.vehicleId,
        fleet: v.fleet, name: v.name, licensePlate: v.licensePlate, make: v.make, lastTransmission: v.lastTransmission, reason: v.reason || 'Offline'
      }, { headers: getHeaders() });
      v.alertId = r.data.id;
      return r.data.id;
    } catch (e) { console.error('Error creating alert:', e); return ''; }
  };

  const saveNotes = async () => {
    if (!selectedAlert || !noteText.trim()) return;
    setSaving(true);
    const id = await ensureAlert(selectedAlert);
    if (id) {
      try {
        const r = await axios.put(`${API_BASE}/api/alerts/${id}/notes`, { note: noteText.trim() }, { headers: getHeaders() });
        const entry = r.data.entry as NoteEntry;
        const updated = [...modalNotes, entry];
        setModalNotes(updated);
        selectedAlert.notes = updated;
        setAlerts([...alerts]);
        setNoteText('');
      } catch (e) { console.error('Error saving notes:', e); }
    }
    setSaving(false);
  };

  useEffect(() => { fetchAlerts(); fetchDeviceMap(); const i = setInterval(fetchAlerts, 5 * 60 * 1000); return () => clearInterval(i); }, []);
  useEffect(() => { if (activeTab === 'history') fetchHistory(); }, [activeTab]);

  const openAlert = (v: AlertVehicle) => { setSelectedAlert(v); setNoteText(''); setModalNotes(Array.isArray(v.notes) ? v.notes : []); };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px', fontSize: '24px', color: '#1a1a2e' }}>🚨 Vehicle Alerts</h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            {activeTab === 'active' ? `Last refreshed ${lastRefresh.toLocaleTimeString('en-NZ')} · Auto-refreshes every 5 min` : 'Search resolved alerts by vehicle, fleet, notes, or platform'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {activeTab === 'active' && (
            <>
              <span style={{ background: alertCountExcludingBattery > 0 ? '#fef2f2' : '#f0fdf4', color: alertCountExcludingBattery > 0 ? '#dc2626' : '#16a34a', padding: '8px 16px', borderRadius: '20px', fontWeight: 600, fontSize: '14px' }}>
                {alertCountExcludingBattery} Alert{alertCountExcludingBattery !== 1 ? 's' : ''}
              </span>
              <button onClick={fetchAlerts} disabled={loading} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px', opacity: loading ? 0.6 : 1 }}>
                {loading ? '⟳ Refreshing...' : '⟳ Refresh'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e5e7eb' }}>
        {(['active', 'history'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '12px 24px', border: 'none', background: 'none', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: activeTab === tab ? '#2563eb' : '#999', borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent', marginBottom: '-2px' }}>
            {tab === 'active' ? `🔴 Active Alerts (${alertCountExcludingBattery})` : `📁 History (${history.length})`}
          </button>
        ))}
      </div>

      {activeTab === 'active' ? (
        <>
          {alerts.length > 0 && (<>
            {/* KPI Bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'Total Alerts', value: alertCountExcludingBattery, icon: '🚨', color: '#dc2626', bg: '#fef2f2' },
                { label: 'Battery Warnings', value: warningCount, icon: '🔋', color: '#ea580c', bg: '#fff1e6' },
                { label: 'Being Investigated', value: notesCount, icon: '📝', color: '#16a34a', bg: '#f0fdf4' },
                { label: 'Needs Attention', value: alerts.filter(a => !warnings[a.unitId] && (!a.notes || a.notes.length === 0)).length, icon: '👀', color: '#2563eb', bg: '#eff6ff' },
                { label: 'Showing', value: filteredAlerts.length, icon: '📊', color: '#6b7280', bg: '#f9fafb' },
              ].map(kpi => (
                <div key={kpi.label} style={{ background: kpi.bg, borderRadius: '12px', padding: '14px 16px', border: `1px solid ${kpi.color}20`, textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{kpi.icon}</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                  <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{kpi.label}</div>
                </div>
              ))}
            </div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' as const, alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#999', fontWeight: 600, textTransform: 'uppercase' as const }}>Platform</span>
              <div onClick={() => setPlatformFilter(f => f === 'Camel' ? 'all' : 'Camel')} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: platformFilter === 'Camel' ? '#7c3aed' : '#ede9fe', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}>
                <span style={{ fontSize: '14px' }}>🐫</span>
                <span style={{ fontWeight: 600, color: platformFilter === 'Camel' ? '#fff' : '#7c3aed', fontSize: '13px' }}>Camel ({alerts.filter(a => a.platform === 'Camel').length})</span>
              </div>
              <div onClick={() => setPlatformFilter(f => f === 'Starlink' ? 'all' : 'Starlink')} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: platformFilter === 'Starlink' ? '#2563eb' : '#dbeafe', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}>
                <span style={{ fontSize: '14px' }}>⭐</span>
                <span style={{ fontWeight: 600, color: platformFilter === 'Starlink' ? '#fff' : '#2563eb', fontSize: '13px' }}>Starlink ({alerts.filter(a => a.platform === 'Starlink').length})</span>
              </div>
              <div style={{ width: '1px', height: '24px', background: '#e5e7eb' }} />
              <span style={{ fontSize: '12px', color: '#999', fontWeight: 600, textTransform: 'uppercase' as const }}>Hide</span>
              <div onClick={() => setHideBattery(!hideBattery)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: hideBattery ? '#ea580c' : '#fff1e6', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid #ea580c30' }}>
                <span style={{ fontSize: '14px' }}>{hideBattery ? '✅' : '🔋'}</span>
                <span style={{ fontWeight: 600, color: hideBattery ? '#fff' : '#ea580c', fontSize: '13px' }}>Battery Warnings ({warningCount})</span>
              </div>
              <div onClick={() => setHideNotes(!hideNotes)} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: hideNotes ? '#16a34a' : '#f0fdf4', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', border: '1px solid #16a34a30' }}>
                <span style={{ fontSize: '14px' }}>{hideNotes ? '✅' : '📝'}</span>
                <span style={{ fontWeight: 600, color: hideNotes ? '#fff' : '#16a34a', fontSize: '13px' }}>Being Investigated ({notesCount})</span>
              </div>
            </div>
            {/* Fleet Filter */}
            {Object.keys(fleetCounts).length > 1 && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' as const, alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#999', fontWeight: 600, textTransform: 'uppercase' as const }}>Fleet</span>
                <div onClick={() => setFleetFilter('all')} style={{ padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', background: fleetFilter === 'all' ? '#334155' : '#f1f5f9', border: '1px solid #e2e8f0', transition: 'all 0.2s' }}>
                  <span style={{ fontWeight: 600, color: fleetFilter === 'all' ? '#fff' : '#475569', fontSize: '12px' }}>All ({alerts.length})</span>
                </div>
                {Object.entries(fleetCounts).sort((a, b) => b[1] - a[1]).map(([fleet, count]) => (
                  <div key={fleet} onClick={() => setFleetFilter(f => f === fleet ? 'all' : fleet)} style={{ padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', background: fleetFilter === fleet ? '#334155' : '#f1f5f9', border: '1px solid #e2e8f0', transition: 'all 0.2s' }}>
                    <span style={{ fontWeight: 600, color: fleetFilter === fleet ? '#fff' : '#475569', fontSize: '12px' }}>{fleet} ({count})</span>
                  </div>
                ))}
              </div>
            )}
          </>)}
          {loading && filteredAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}><div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div><p>Loading vehicle data...</p></div>
          ) : filteredAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 0', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}><div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div><h3 style={{ margin: '0 0 8px', color: '#16a34a' }}>All Clear!</h3><p style={{ color: '#666', margin: 0 }}>All vehicles are reporting normally.</p></div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
              {filteredAlerts.map((v, i) => (
                <div key={`${v.platform}-${v.unitId}-${i}`} style={{ background: warnings[v.unitId] ? '#fff1e6' : v.notes && v.notes.length > 0 ? '#f0fdf4' : '#fff', borderRadius: '16px', padding: '20px', boxShadow: warnings[v.unitId] ? '0 0 14px rgba(234,88,12,0.5)' : v.notes && v.notes.length > 0 ? '0 0 12px rgba(34,197,94,0.4)' : '0 2px 12px rgba(0,0,0,0.06)', borderLeft: `4px solid ${warnings[v.unitId] ? '#ea580c' : v.notes && v.notes.length > 0 ? '#22c55e' : urgencyColor(v.hoursAgo)}`, transition: 'all 0.2s', cursor: 'pointer' }}
                  onClick={() => openAlert(v)}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <span style={{ background: v.platform === 'Camel' ? '#ede9fe' : '#dbeafe', color: v.platform === 'Camel' ? '#7c3aed' : '#2563eb', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                      {v.platform === 'Camel' ? '🐫' : '⭐'} {v.platform}
                    </span>
                    <span style={{ background: `${urgencyColor(v.hoursAgo)}15`, color: urgencyColor(v.hoursAgo), padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>
                      {v.hoursAgo < 0 ? 'Never' : v.hoursAgo < 48 ? `${v.hoursAgo}h ago` : `${Math.round(v.hoursAgo / 24)}d ago`}
                    </span>
                  </div>
                  <h3 style={{ margin: '0 0 12px', fontSize: '16px', color: '#1a1a2e' }}>{v.licensePlate || v.name || v.vehicleId}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', color: '#555' }}>
                    <div><span style={{ color: '#999' }}>Vehicle ID:</span> {v.vehicleId}</div>
                    <div><span style={{ color: '#999' }}>Fleet:</span> {v.fleet || '—'}</div>
                    <div><span style={{ color: '#999' }}>Make:</span> {v.make || '—'}</div>
                    <div><span style={{ color: '#999' }}>Unit ID:</span> {v.unitId}</div>
                  </div>
                  {v.platform === 'Camel' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px', marginTop: '10px', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div><span style={{ color: '#999' }}>VIN:</span> <span style={{ fontWeight: 500 }}>{v.vin || '—'}</span></div>
                      <div><span style={{ color: '#999' }}>VBAT:</span> <span style={{ fontWeight: 600, color: v.vbat && v.vbat < 12 ? '#dc2626' : '#333' }}>{v.vbat ? `${v.vbat}V` : '—'}</span></div>
                      <div><span style={{ color: '#999' }}>CSS:</span> <span style={{ fontWeight: 600, color: v.css && v.css < 10 ? '#dc2626' : '#333' }}>{v.css ?? '—'}</span></div>
                      <div><span style={{ color: '#999' }}>SPDK:</span> <span style={{ fontWeight: 500 }}>{v.spdk ?? '—'}</span></div>
                    </div>
                  )}
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0', fontSize: '13px', color: '#999' }}>
                    📡 Last: <span style={{ color: '#333', fontWeight: 500 }}>{v.lastTransmission ? formatTime(v.lastTransmission) : 'Never'}</span>
                    {v.notes && v.notes.length > 0 && <span style={{ marginLeft: '12px', color: '#2563eb' }}>📝 {v.notes.length} note{v.notes.length !== 1 ? 's' : ''}</span>}
                  </div>
                  {warnings[v.unitId] && (
                    <div style={{ marginTop: '8px', padding: '6px 10px', background: '#fef3c7', borderRadius: '6px', fontSize: '12px', color: '#92400e', fontWeight: 500 }}>
                      ⚠️ {warnings[v.unitId].lowVin ? `Low VIN: ${warnings[v.unitId].lowVin}V` : ''}{warnings[v.unitId].lowVin && warnings[v.unitId].eid9 ? ' · ' : ''}{warnings[v.unitId].eid9 ? 'EID 9 detected' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* History Tab */
        <>
          <div style={{ marginBottom: '20px' }}>
            <input value={historySearch} onChange={e => setHistorySearch(e.target.value)}
              placeholder="Search by vehicle, fleet, platform, reason..."
              style={{ padding: '12px 16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '14px', width: '400px' }} />
          </div>
          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>Loading history...</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
              <p style={{ color: '#666' }}>No resolved alerts yet.</p>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Platform</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Vehicle</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Fleet</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Alert Created</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Resolved</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Resolved By</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {history.filter(h => { if (!historySearch) return true; const s = historySearch.toLowerCase(); return (h.vehicle_name || '').toLowerCase().includes(s) || (h.unit_id || '').toLowerCase().includes(s) || (h.fleet || '').toLowerCase().includes(s) || (h.platform || '').toLowerCase().includes(s) || ((h as any).reason || '').toLowerCase().includes(s) || (h.resolved_by || '').toLowerCase().includes(s); }).map(h => (
                    <tr key={h.id} onClick={() => openVehicleHistory(h.unit_id)} style={{ borderTop: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => (e.currentTarget.style.background = '#f0f4ff')} onMouseLeave={e => (e.currentTarget.style.background = '')}>
                      <td style={{ padding: '12px 16px' }}>{h.platform === 'Camel' ? '🐫' : '⭐'} {h.platform}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 500 }}>{h.vehicle_name || h.unit_id}</td>
                      <td style={{ padding: '12px 16px' }}>{h.fleet || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>{formatTime(h.created_at)}</td>
                      <td style={{ padding: '12px 16px' }}>{formatTime(h.resolved_at)}</td>
                      <td style={{ padding: '12px 16px' }}>{h.resolved_by}</td>
                      <td style={{ padding: '12px 16px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{Array.isArray(h.notes) ? (h.notes.length > 0 ? h.notes[h.notes.length - 1].text : '—') : (h.notes || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedAlert && (() => {
        const v = selectedAlert;
        const device = getDevice(v.unitId);
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedAlert(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '32px', maxWidth: '520px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ background: v.platform === 'Camel' ? '#ede9fe' : '#dbeafe', color: v.platform === 'Camel' ? '#7c3aed' : '#2563eb', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                    {v.platform === 'Camel' ? '🐫' : '⭐'} {v.platform}
                  </span>
                  <span style={{ background: `${urgencyColor(v.hoursAgo)}15`, color: urgencyColor(v.hoursAgo), padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>
                    {v.hoursAgo < 0 ? 'Never' : v.hoursAgo < 48 ? `${v.hoursAgo}h ago` : `${Math.round(v.hoursAgo / 24)}d ago`}
                  </span>
                </div>
                <button onClick={() => setSelectedAlert(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#999' }}>✕</button>
              </div>
              <h2 style={{ margin: '0 0 20px', fontSize: '20px', color: '#1a1a2e' }}>{v.licensePlate || v.name || v.vehicleId}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', color: '#555', marginBottom: '16px' }}>
                <div><span style={{ color: '#999' }}>Vehicle ID:</span> {v.vehicleId}</div>
                <div><span style={{ color: '#999' }}>Fleet:</span> {v.fleet || '—'}</div>
                <div><span style={{ color: '#999' }}>Make:</span> {v.make || '—'}</div>
                <div><span style={{ color: '#999' }}>Unit ID:</span> {v.unitId}</div>
              </div>
              <div style={{ marginBottom: '20px', fontSize: '13px', color: '#999' }}>
                📡 Last Transmission: <span style={{ color: '#333', fontWeight: 500 }}>{v.lastTransmission ? formatTime(v.lastTransmission) : 'Never reported'}</span>
              </div>
              <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                <p style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: '#555' }}>📋 Device Details</p>
                {device ? (
                  <>{['Client','Name','License Plate','IMEI','Serial','ICCID','Device Type','Manufacturer','Data Plan','Model/Version','Hex Value'].map((label, idx) => {
                    const vals = [device.client_name, v.name||device.vehicle_id, v.licensePlate||device.licensePlate, device.imei_number, device.serial_number, device.iccid, device.device_type, device.device_manufacturer, device.sim_data_plan, device.model_version, device.hex_value];
                    return <DetailRow key={label} label={label} value={vals[idx]} />;
                  })}</>
                ) : (
                  <p style={{ margin: 0, fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No device record found — device may not be added to All Devices yet.</p>
                )}
              </div>
              {/* Notes */}
              <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '16px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: '#92400e' }}>📝 Investigation Notes ({modalNotes.length})</p>
                {modalNotes.length > 0 && (
                  <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {modalNotes.map((n, i) => (
                      <div key={i} style={{ background: '#fff', borderRadius: '8px', padding: '10px 12px', border: '1px solid #f0e6c8' }}>
                        <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600 }}>{n.user}</span>
                          <span style={{ color: '#b5a06b' }}>{formatTime(n.timestamp)}</span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#333' }}>{n.text}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..."
                    onKeyDown={e => e.key === 'Enter' && !saving && saveNotes()}
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
                  <button onClick={saveNotes} disabled={saving || !noteText.trim()} style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '13px', opacity: saving || !noteText.trim() ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                    {saving ? '...' : '💾 Save'}
                  </button>
                </div>
              </div>
              {onNavigateToPlatform && (
                <button onClick={() => { setSelectedAlert(null); onNavigateToPlatform(v.platform === 'Starlink' ? 'starlink' : 'camel', v.unitId); }}
                  style={{ marginTop: '16px', width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                  🖥️ Open CMD — {v.platform}
                </button>
              )}
              <button onClick={() => setBatteryModalVehicle(v)}
                style={{ marginTop: '10px', width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                🔋 Battery Health Chart
              </button>
            </div>
          </div>
        );
      })()}

    {/* Vehicle History KPI Modal */}
    {vehicleHistory !== null && (() => {
      const items = vehicleHistory;
      const vName = items[0]?.vehicle_name || items[0]?.unit_id || 'Vehicle';
      const vFleet = items[0]?.fleet || '—';
      const vPlatform = items[0]?.platform || '';
      const totalIncidents = items.length;
      const resolved = items.filter((i: any) => i.status === 'resolved');
      const active = items.filter((i: any) => i.status === 'active');
      const totalDowntimeMs = resolved.reduce((sum: number, i: any) => {
        if (i.created_at && i.resolved_at) return sum + (new Date(i.resolved_at).getTime() - new Date(i.created_at).getTime());
        return sum;
      }, 0);
      const avgDowntimeMs = resolved.length > 0 ? totalDowntimeMs / resolved.length : 0;
      const fmtDuration = (ms: number) => {
        if (ms < 60000) return '< 1m';
        const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
      };
      const totalNotes = items.reduce((sum: number, i: any) => sum + (Array.isArray(i.notes) ? i.notes.length : 0), 0);
      const longest = resolved.reduce((max: number, i: any) => {
        if (i.created_at && i.resolved_at) { const d = new Date(i.resolved_at).getTime() - new Date(i.created_at).getTime(); return d > max ? d : max; }
        return max;
      }, 0);

      return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setVehicleHistory(null)}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '680px', maxHeight: '85vh', overflow: 'auto', padding: '28px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px' }}>{vPlatform === 'Camel' ? '🐫' : '⭐'} {vName}</h2>
                <span style={{ color: '#888', fontSize: '13px' }}>Fleet: {vFleet} • Unit: {items[0]?.unit_id}</span>
              </div>
              <button onClick={() => setVehicleHistory(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {vehicleHistoryLoading ? <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p> : (<>
              {/* Battery Status */}
              {vehicleWarning && (vehicleWarning.eid9 || (vehicleWarning.lowVin !== undefined && vehicleWarning.lowVin <= 10.10)) && (
                <div style={{ background: '#fff7ed', border: '1px solid #fb923c', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>🔋</span>
                  <div>
                    <div style={{ fontWeight: 700, color: '#ea580c', fontSize: '14px' }}>Battery Issue Detected</div>
                    <div style={{ fontSize: '12px', color: '#9a3412' }}>
                      {vehicleWarning.eid9 && <span>⚠️ EID 9 — Battery disconnected. </span>}
                      {vehicleWarning.lowVin !== undefined && vehicleWarning.lowVin <= 10.10 && <span>⚠️ Low voltage: {vehicleWarning.lowVin.toFixed(2)}V</span>}
                    </div>
                  </div>
                </div>
              )}
              {/* KPI Tiles */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                {[
                  { label: 'Total Incidents', value: totalIncidents, icon: '🚨', bg: '#fef2f2', color: '#dc2626' },
                  { label: 'Currently Active', value: active.length, icon: '🔴', bg: active.length > 0 ? '#fef2f2' : '#f0fdf4', color: active.length > 0 ? '#dc2626' : '#16a34a' },
                  { label: 'Resolved', value: resolved.length, icon: '✅', bg: '#f0fdf4', color: '#16a34a' },
                  { label: 'Total Downtime', value: fmtDuration(totalDowntimeMs), icon: '⏱️', bg: '#eff6ff', color: '#2563eb' },
                  { label: 'Avg Resolution', value: fmtDuration(avgDowntimeMs), icon: '📊', bg: '#f5f3ff', color: '#7c3aed' },
                  { label: 'Longest Outage', value: fmtDuration(longest), icon: '📈', bg: '#fff7ed', color: '#ea580c' },
                ].map((t, i) => (
                  <div key={i} style={{ background: t.bg, borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', marginBottom: '4px' }}>{t.icon}</div>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: t.color }}>{t.value}</div>
                    <div style={{ fontSize: '11px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t.label}</div>
                  </div>
                ))}
              </div>

              {totalNotes > 0 && <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#16a34a', fontWeight: 600 }}>📝 {totalNotes} total notes across all incidents</div>}

              {/* Timeline */}
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '10px' }}>Incident Timeline</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map((item: any, idx: number) => {
                  const dur = item.created_at && item.resolved_at ? new Date(item.resolved_at).getTime() - new Date(item.created_at).getTime() : 0;
                  const noteCount = Array.isArray(item.notes) ? item.notes.length : 0;
                  const isActive = item.status === 'active';
                  return (
                    <div key={idx} style={{ background: isActive ? '#fef2f2' : '#fafafa', borderRadius: '10px', padding: '12px 16px', border: `1px solid ${isActive ? '#fca5a5' : '#eee'}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: isActive ? '#dc2626' : '#333' }}>
                          {isActive ? '🔴 Active' : '✅ Resolved'} — {formatTime(item.created_at)}
                          {item.reason && item.reason !== 'Offline' && <span style={{ marginLeft: '8px', background: '#fff7ed', color: '#ea580c', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>🔋 {item.reason}</span>}
                          {(!item.reason || item.reason === 'Offline') && <span style={{ marginLeft: '8px', background: '#f0f4ff', color: '#2563eb', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 700 }}>📡 Offline</span>}
                        </span>
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          {isActive ? 'Ongoing' : `${fmtDuration(dur)} • ${item.resolved_by}`}
                        </span>
                      </div>
                      {noteCount > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '12px', color: '#666' }}>
                          {item.notes.map((n: any, ni: number) => (
                            <div key={ni} style={{ padding: '3px 0', borderTop: ni > 0 ? '1px solid #eee' : 'none' }}>
                              <span style={{ color: '#999' }}>{n.user} • {n.timestamp}</span>: {n.text}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>)}
          </div>
        </div>
      );
    })()}
    
    {/* Battery Health Modal */}
    <BatteryHealthModal
      isOpen={!!batteryModalVehicle}
      onClose={() => setBatteryModalVehicle(null)}
      unitId={batteryModalVehicle?.unitId || ''}
      vehicleName={batteryModalVehicle?.name || batteryModalVehicle?.licensePlate || ''}
      platform={batteryModalVehicle?.platform?.toLowerCase() as 'camel' | 'starlink'}
    />
  </div>
  );
};

export default Alerts;
